/*
 * Standard-rollover history fallback (MMG-4304): saved results reference the original point IDs, so
 * after a standard copy the GUID-keyed history features match via each point's sourceMeasurementPointID
 * chain. Full rules: Beacon-MMG/docs/measurement-points-standards.md §11.
 */
import {
    ImeasurementPoint,
    ImeasurementPointAnswer,
    ImeasurementPointList,
    ImeasurementPointResult
} from '../models';

interface ImeasurementPointsByID {
    [key: string]: ImeasurementPoint;
}

// copies of copies are allowed (e.g. 2028 -> 2024 -> 2012); guard against bad data producing loops
const MAX_ANCESTOR_DEPTH = 10;

/* walk sourceMeasurementPointID transitively: the point's original, that original's original, ... */
export const getAncestorIDs = (pointID: string, measurementPointsByID: ImeasurementPointsByID): string[] => {
    const ancestors: string[] = [];
    let currentID = measurementPointsByID[pointID]?.sourceMeasurementPointID;
    while (
        currentID !== undefined &&
        currentID.length > 0 &&
        currentID !== pointID &&
        !ancestors.includes(currentID) &&
        ancestors.length < MAX_ANCESTOR_DEPTH
    ) {
        ancestors.push(currentID);
        currentID = measurementPointsByID[currentID]?.sourceMeasurementPointID;
    }
    return ancestors;
};

/* map every ancestor ID of every point in the target list to its current point */
export const buildPointTranslationMap = (
    targetList: ImeasurementPointList,
    measurementPointsByID: ImeasurementPointsByID
): { [oldPointID: string]: ImeasurementPoint } => {
    const map: { [oldPointID: string]: ImeasurementPoint } = {};
    targetList.measurementPointTabs.forEach(tab => {
        Object.values(tab.measurementPoints || {}).forEach(point => {
            getAncestorIDs(point.id, measurementPointsByID).forEach(ancestorID => {
                if (map[ancestorID] === undefined) {
                    map[ancestorID] = point;
                }
            });
        });
    });
    return map;
};

const translateSelectOptionID = (
    answer: ImeasurementPointAnswer,
    oldPoint: ImeasurementPoint | undefined,
    newPoint: ImeasurementPoint
): string | undefined => {
    const oldOption = oldPoint?.selectOptions?.find(option => option.id === answer.measurementPointSelectOptionID);
    if (!oldOption) {
        return undefined;
    }
    const newOptions = newPoint.selectOptions || [];
    const exactMatch = newOptions.find(option => option.value === oldOption.value && option.label === oldOption.label);
    if (exactMatch) {
        return exactMatch.id;
    }
    return newOptions.find(option => option.label === oldOption.label)?.id;
};

/* same-list and unrelated-list results are returned unchanged (same reference); never mutates input */
export const translateResultToList = <T extends ImeasurementPointResult>(
    result: T | undefined,
    targetList: ImeasurementPointList,
    measurementPointsByID: ImeasurementPointsByID
): T | undefined => {
    if (result === undefined || targetList.id.length === 0 || result.measurementPointListID === targetList.id) {
        return result;
    }

    const map = buildPointTranslationMap(targetList, measurementPointsByID);
    let translatedAny = false;
    const translatedAnswers = (result.measurementPointAnswers || []).map(answer => {
        const newPoint = map[answer.measurementPointID];
        if (newPoint === undefined) {
            return answer;
        }
        translatedAny = true;
        const translated: ImeasurementPointAnswer = { ...answer, measurementPointID: newPoint.id };
        if (answer.measurementPointSelectOptionID !== undefined) {
            const oldPoint = measurementPointsByID[answer.measurementPointID];
            const newOptionID = translateSelectOptionID(answer, oldPoint, newPoint);
            if (newOptionID !== undefined) {
                translated.measurementPointSelectOptionID = newOptionID;
            } else {
                // a stale option ID would count as "answered" while rendering blank - drop the value instead
                delete translated.measurementPointSelectOptionID;
            }
        }
        return translated;
    });

    if (!translatedAny) {
        return result;
    }

    return { ...result, measurementPointAnswers: translatedAnswers };
};
